import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FaRegTrashAlt, FaPen } from 'react-icons/fa';
import { generatePath, Link } from 'react-router-dom';
import { InferType, object, string } from 'yup';

import { TenantReadModel, Role, PersonReadModel } from '@client/api';
import { Button, isDefinedString, Select, TableCell, TableRow, Text } from '@client/toolkit';
import { CompaniesPaths } from '../../../../companies/companies.router';
import { useTenants } from '../../../../../hooks/useTenants/useTenants';
import { TenantSelectionEnum } from '../../../../../redux';

const schema = object({
  id: string().nullable(),
  stakeholderId: string().required(),
  role: string<Role>().optional(),
  representativeId: string().nullable(),
});

type FormValues = InferType<typeof schema>;

export const agreementPartySchema = schema;
export type AgreementPartyFormData = FormValues;

interface AgreementPartyItemFormProps {
  fieldIndex: number;
  stakeholder?: TenantReadModel;
  role?: Role;
  isFetching?: boolean;
  onRemove?: (fieldIndex: number) => void;
  onEdit?: () => void;
  availableContacts?: PersonReadModel[];
  selectedRepresentativeId?: string | null;
  onRepresentativeChange?: (fieldIndex: number, representativeId: string | null) => void;
  contactOnlyMode?: boolean;
}

export function AgreementPartyItemForm({
  fieldIndex,
  stakeholder,
  role,
  isFetching,
  onRemove,
  onEdit,
  availableContacts,
  selectedRepresentativeId,
  onRepresentativeChange,
  contactOnlyMode,
}: AgreementPartyItemFormProps) {
  const { t: tType } = useTranslation('type');
  const { t: tAgreements } = useTranslation('agreements');
  const { selectedTenant } = useTenants();
  const currentTenantId = selectedTenant && selectedTenant !== TenantSelectionEnum.All ? selectedTenant.id : null;
  const isDisabled = typeof onRemove !== 'function' || isFetching || role === 'Owner' || role === 'Manager';

  const selectedRepresentative = useMemo(
    () => availableContacts?.find((c) => c.id === selectedRepresentativeId) ?? null,
    [availableContacts, selectedRepresentativeId],
  );

  const isOwnerOrManager = role === 'Owner' || role === 'Manager';

  return (
    <TableRow verticalAlign="middle">
      <TableCell loading={isFetching}>
        <Text isEmpty={!isDefinedString(stakeholder?.id)}>
          {stakeholder?.id === currentTenantId ? (
            <Link
              to="/profile"
              className="hover:text-brand-primary transition-colors"
            >
              <strong className="whitespace-nowrap">{stakeholder.name}</strong>
            </Link>
          ) : stakeholder?.companyId ? (
            <Link
              to={generatePath(CompaniesPaths.Company, { id: stakeholder.companyId })}
              className="hover:text-brand-primary transition-colors"
            >
              <strong className="whitespace-nowrap">{stakeholder.name}</strong>
            </Link>
          ) : (
            <strong className="whitespace-nowrap">{stakeholder?.name}</strong>
          )}
        </Text>
      </TableCell>

      <TableCell loading={isFetching}>
        <Text>
          {role ? tType(`RelationshipType.${role}`) : ''}
        </Text>
      </TableCell>

      <TableCell loading={isFetching}>
        {availableContacts && availableContacts.length > 0 ? (
          <Select
            size="medium"
            value={selectedRepresentativeId ?? ''}
            onChange={(e) => {
              onRepresentativeChange?.(fieldIndex, e.target.value || null);
            }}
            disabled={isFetching}
          >
            <option value="">{tAgreements('agreement.selectContactPerson')}</option>
            {availableContacts.map((person) => (
              <option key={person.id} value={person.id}>
                {person.name}
              </option>
            ))}
          </Select>
        ) : (
          <Text as="div" isEmpty>—</Text>
        )}
      </TableCell>

      <TableCell loading={isFetching}>
        <Text as="div" isEmpty={!selectedRepresentative?.email}>
          {selectedRepresentative?.email ?? '—'}
        </Text>
      </TableCell>
      <TableCell loading={isFetching}>
        <Text as="div" isEmpty={!selectedRepresentative?.phone}>
          {selectedRepresentative?.phone ?? '—'}
        </Text>
      </TableCell>

      {!contactOnlyMode && (
        <TableCell loading={isFetching}>
          <div className="flex items-center justify-end">
            {!isOwnerOrManager && (
              <Button
                className="ml-2 text-brand-primary bg-surface-elevated size-9"
                size="xs"
                color="custom"
                disabled={isFetching}
                onClick={onEdit}
              >
                <FaPen />
              </Button>
            )}
            <Button
              className="ml-2 text-brand-primary bg-surface-elevated size-9"
              size="xs"
              color="custom"
              disabled={isDisabled}
              onClick={() => {
                if (typeof onRemove !== 'function') {
                  return;
                }
                onRemove(fieldIndex);
              }}
            >
              <FaRegTrashAlt />
            </Button>
          </div>
        </TableCell>
      )}
    </TableRow>
  );
}
